import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {createUseStyles, useTheme} from "react-jss";
import {EngageAppContext} from "../../App";
import {ChatBubble} from "./ChatBubble";
import clsx from "clsx";
import {Virtuoso} from "react-virtuoso";
import {Theme} from "engage-client/theme";
import {ChatContext, ChatUIState} from "../../contexts/ChatContext";


const useStyles = createUseStyles<Theme>((theme) => ({
    chatHistory: {
        opacity: props => props.chatUIState !== ChatUIState.none ? 1 : 0,
        transition: "opacity 250ms ease-in-out",
        padding: [20,5,10],
    },
    popup: {
        backgroundImage: ({chatUIState:cs}) => `linear-gradient(to bottom, transparent, ${cs === ChatUIState.focused ? theme.chat.background.focused : theme.chat.background.unfocused} 20px)`,
        position: "absolute",
        bottom: 58, // size of the chat input + padding
        right: 0,
        left: 0,
        height: props => props.popupHeight,
        [theme.breakpoints.down("md")] : {
            height: 300
        },
        pointerEvents: ({chatUIState:cs}) => cs === ChatUIState.focused ? "auto" : "none"
    },
    fixed:{
        display:"flex",
        flex: 1,
    }
}));

type ChatHistoryProps = {
    chatUIState: ChatUIState
    popup: boolean
}

const ChatHistory = ({chatUIState, popup}:ChatHistoryProps) => {
    const {appState, user} = useContext(EngageAppContext.current);
    const {chatReadCount} = useContext(ChatContext);
    const [frozenLastRead] = useState(chatReadCount === appState.chat.entries.length ? -1 : chatReadCount);

    // @ts-ignore - visualViewport only exists in certain browsers and isn't defined in our current lib.dom.ts
    const [viewportHeight, setViewportHeight] = useState<number>(window?.visualViewport?.height ?? 0);

    const renderRow = useCallback((index)=> {
        const entry = appState.chat.entries[index];
        if(entry) {
            return (
                <ChatBubble currentPlayer={entry.userID === user?.id}
                            nickname={entry.nickname}
                            text={entry.text}
                            key={index}
                            newLine={!popup && frozenLastRead === index}
                            verified={entry.verified}
                            team={entry.teamID}
                />
            );
        } else {
            return <></>;
        }

    },[appState.chat.entries, frozenLastRead, popup, user]);

    useEffect(() => {
        const visualViewport = (window as any).visualViewport;
        const callback = () => {
            setViewportHeight(visualViewport.height);
        };
        visualViewport?.addEventListener("resize", callback);
        return () => {visualViewport?.removeEventListener("resize",callback);};
    },[]);

    //on devices that support it, use visualViewport height to determine size. This helps with on screen keyboards.
    const popupHeight = viewportHeight > 0 ? Math.min(400, viewportHeight - 58) : 400;
    const classes = useStyles({chatUIState, popupHeight});

    return <div className={clsx(classes.chatHistory, popup ? classes.popup : classes.fixed)}>
        <PureChatHistory popup={popup}
                            chatUIState={chatUIState}
                            renderRow={renderRow}
                            totalEntries={appState.chat.entries.length}/>
    </div>;

};

type PureChatHistoryProps = ChatHistoryProps & {
    renderRow: (index:number) => JSX.Element
    totalEntries: number
}

const useScrollStyles = createUseStyles<Theme>((theme) => ({
    scrollArea: {
        scrollbarColor: "#562E70 transparent",
        "&::-webkit-scrollbar": {
            backgroundColor: "transparent",
            width: 8,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.chat.scrollBar,
            borderRadius: 4,
        }
    },
}));

const PureChatHistory = (props:PureChatHistoryProps) => {
    const {renderRow, totalEntries} = props;
    const [atBottom, setAtBottom] = useState(true);
    const chatList = useRef<any>(null);
    const classes = useScrollStyles();

    useEffect(()=>{
        if(atBottom && totalEntries > 0) {
            chatList.current?.scrollToIndex({index:totalEntries-1, align:"end"});
        }
    });

    return (
        <Virtuoso totalCount={totalEntries}
                  item={renderRow}
                  style={{width: "100%"}}
                  className={classes.scrollArea}
                  rangeChanged={({endIndex}) => {
                      //Aggressively assume we're near the bottom
                     setAtBottom(endIndex+2 > totalEntries);
                  }}
                  ref={chatList}
        />
    );
};

export default ChatHistory;